import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Tag {
  id: string;
  name: string;
  color: string;
  createdAt: Date;
}

interface TagsState {
  tags: Tag[];
  historyTags: Tag[]; // 历史使用的标签，最多保存30个
  addTag: (name: string, color?: string) => Tag;
  addToHistory: (tag: Tag) => void;
  searchHistoryTags: (query: string) => Tag[];
  getTagByName: (name: string) => Tag | undefined;
}

const defaultColors = [
  'var(--color-primary)', // 主题色
  'var(--color-tag-blue)', // 蓝色
  'var(--color-tag-green)', // 绿色
  'var(--color-tag-yellow)', // 黄色
  'var(--color-tag-red)', // 红色
  'var(--color-tag-purple)', // 紫色
  'var(--color-tag-cyan)', // 青色
  'var(--color-tag-orange)', // 橙色
];

export const useTagsStore = create<TagsState>()(
  persist(
    (set, get) => ({
      tags: [], // This is now only for backward compatibility during migration.
      historyTags: [],

      addTag: (name: string, color?: string) => {
        const existingTag = get().historyTags.find(tag => tag.name.toLowerCase() === name.toLowerCase());
        if (existingTag) {
          get().addToHistory(existingTag);
          return existingTag;
        }

        const newTag: Tag = {
          id: Date.now().toString(),
          name,
          color: color || defaultColors[get().historyTags.length % defaultColors.length],
          createdAt: new Date(),
        };

        get().addToHistory(newTag);
        return newTag;
      },

      addToHistory: (tag: Tag) => {
        set(state => {
          const filtered = state.historyTags.filter(t => t.id !== tag.id);
          const newHistory = [tag, ...filtered].slice(0, 30); // 保持最多30个
          return { historyTags: newHistory };
        });
      },

      searchHistoryTags: (query: string) => {
        const historyTags = get().historyTags;
        if (!query.trim()) return historyTags.slice(0, 10); // 无查询时返回前10个

        return historyTags.filter(tag =>
          tag.name.toLowerCase().includes(query.toLowerCase())
        );
      },

      getTagByName: (name: string) => {
        return get().historyTags.find(tag => tag.name.toLowerCase() === name.toLowerCase());
      },

      // The following functions are kept for migration purposes and can be removed later.
      deleteTag: () => {},
      updateTag: () => {},
      getTagById: (id: string) => {
        // This is a mock implementation for migration.
        const allTags = get().tags.concat(get().historyTags);
        return allTags.find(tag => tag.id === id);
      },
      getTagsByIds: (ids: string[]) => {
        const allTags = get().tags.concat(get().historyTags);
        const uniqueTags = Array.from(new Map(allTags.map(tag => [tag.id, tag])).values());
        return ids.map(id => uniqueTags.find(tag => tag.id === id)).filter(Boolean) as Tag[];
      },
    }),
    {
      name: 'tags-store',
    }
  )
);